import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Heat, Player } from '@iziping/common';
import { HeatStatus } from '@iziping/common';
import { useTranslation } from 'react-i18next';
import LoadingBar from '../../../components/shared/LoadingBar';
import { InputIcon } from '@iziping/ui';
import { Search } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_CACHE } from '@/model/endpoints';

import {
    useCreateTournamentRegistration,
    useGetTournament,
    useGetTournamentRegistrations,
    useGetTournamentViewToken,
    useRemoveTournamentRegistration,
    useRevokeViewToken,
    useSendEmailsToPlayers,
} from '../hooks/tournamentHooks';
import { useSearchPlayers } from '../../../components/players/playerHooks';
import { useNotification } from '@iziping/ui';
import { Info, Mail, QrCode, UserMinus, X } from 'lucide-react';
import { formatDateLiteral } from '@iziping/common';
import { QRCodeSVG } from 'qrcode.react';
import TabBar from '../../../components/shared/TabBar';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import type { Tab } from '../../../components/shared/TabBar';
import PreRegistrationListPage from './PreRegistrationListPage';
import TournamentEmailConfig from './TournamentEmailConfig';
import TournamentPreRegistrationConfig from './TournamentPreRegistrationConfig';
import TournamentPage from './TournamentPage';
import TournamentMembers from './TournamentMembers';
import TournamentTablesPage from './TournamentTablesPage';
import TournamentSurveyConfig from './TournamentSurveyConfig';
import { useTournamentStore } from '../../../service/useTournamentStore';

const TournamentDetail: React.FC = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { id, tab } = useParams();
    const tournamentId = id ? parseInt(id, 10) : 0;
    const { data: tournament, isLoading, isError } = useGetTournament(tournamentId);
    const { selectTournament } = useTournamentStore();
    const activeTab = tab ?? 'management';
    const setActiveTab = (key: string) => navigate(`/tournaments/${id}/${key}`);
    const [search, setSearch] = useState('');
    const [showQr, setShowQr] = useState(false);
    const [showSendEmailModal, setShowSendEmailModal] = useState(false);

    const tabs: Tab[] = [
        { key: 'management', label: t('tournament.tab.management') },
        { key: 'configuration', label: t('tournament.tab.configuration') },
        { key: 'tables', label: t('tournament.tab.tables') },
        { key: 'preregistrations', label: t('tournament.tab.preregistrations') },
        {
            key: 'preregistrationConfig',
            label: t('tournament.tab.preregistrationConfig'),
        },
        { key: 'email', label: t('tournament.tab.email') },
        { key: 'survey', label: t('tournament.tab.survey') },
        { key: 'members', label: t('tournament.tab.members') },
    ];

    useEffect(() => {
        if (tournament) selectTournament(tournament);
    }, [tournament]);

    const { notify } = useNotification();
    const { data: viewToken, refetch: fetchViewToken } = useGetTournamentViewToken(tournamentId);
    const revokeViewToken = useRevokeViewToken();
    const sendEmails = useSendEmailsToPlayers(tournamentId);

    const handleShowQr = async () => {
        await fetchViewToken();
        setShowQr(true);
    };

    const handleRevokeViewToken = async () => {
        try {
            await revokeViewToken.mutateAsync(tournamentId);
            setShowQr(false);
            notify('Lien désactivé', 'success');
        } catch {
            notify('Erreur lors de la désactivation', 'error');
        }
    };

    const viewUrl = viewToken ? `${window.location.origin}/tournament/${tournamentId}/view#${viewToken.token}` : null;

    const {
        data: registrations,
        isLoading: registrationsIsLoading,
        isError: registrationsIsError,
    } = useGetTournamentRegistrations(tournamentId);

    const { data: players, isLoading: playersLoading, error: playersError } = useSearchPlayers(search);

    const removeRegistration = useRemoveTournamentRegistration();

    const handleHeatClick = (heatId: number) => {
        navigate(`/heats/management/${heatId}`);
    };

    const handlePlayerClick = (playerId: number) => {
        navigate(`/tournaments/${id}/players/${playerId}`);
    };

    const registerPlayerMutation = useCreateTournamentRegistration();
    const handleRegisterClick = async (tournamentId: number, playerId: number) => {
        try {
            await registerPlayerMutation.mutateAsync({
                tournamentId: tournamentId,
                playerId: playerId,
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_CACHE.TOURNAMENT_REGISTRATIONS],
            });
            setSearch('');
            notify(t('tournament.player.registered'), 'success');
        } catch (error: any) {
            notify(t(error.error), 'error');
        }
    };

    const allHeatsFinished =
        (tournament?.heats?.length ?? 0) > 0 &&
        (tournament?.heats.every((h) => h.status === HeatStatus.FINISHED) ?? false);

    const handleSendEmails = async () => {
        try {
            await sendEmails.mutateAsync();
            notify(t('tournament.email.send.success'), 'success');
        } catch {
            notify(t('tournament.email.send.error'), 'error');
        } finally {
            setShowSendEmailModal(false);
        }
    };

    const handleRemoveRegistration = async (playerId: number) => {
        try {
            await removeRegistration.mutateAsync({
                id: id ?? '',
                playerId: playerId,
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_CACHE.TOURNAMENT_REGISTRATIONS],
            });
            notify(t('tournament.player.unregistered'), 'success');
        } catch (error: any) {
            notify(t(error.error), 'error');
        }
    };

    if (isLoading) return <div>Loading...</div>;
    if (isError) return <div>Error fetching data</div>;

    if (registrationsIsLoading) return <div>Loading...</div>;
    if (registrationsIsError) return <div>Error fetching data</div>;

    return (
        tournament && (
            <div className={'page'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <h2>
                        {tournament.name} ({formatDateLiteral(tournament.date.toString())})
                    </h2>
                    <QrCode style={{ cursor: 'pointer' }} onClick={handleShowQr} />
                    {allHeatsFinished && (
                        <Mail
                            style={{
                                cursor: 'pointer',
                                color: sendEmails.isPending ? '#9ca3af' : '#2563eb',
                            }}
                            onClick={() => !sendEmails.isPending && setShowSendEmailModal(true)}
                        />
                    )}
                </div>
                <ConfirmationModal
                    isOpen={showSendEmailModal}
                    title={t('tournament.email.send.confirm.title')}
                    message={t('tournament.email.send.confirm.message')}
                    confirmLabel={t('tournament.email.send.confirm.button')}
                    onConfirm={handleSendEmails}
                    onCancel={() => setShowSendEmailModal(false)}
                />

                <TabBar tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />

                {showQr && viewUrl && (
                    <div
                        style={{
                            position: 'fixed',
                            inset: 0,
                            background: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000,
                        }}
                    >
                        <div
                            style={{
                                background: 'white',
                                borderRadius: '12px',
                                padding: '24px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '16px',
                                maxWidth: '320px',
                                width: '100%',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    alignItems: 'center',
                                }}
                            >
                                <span style={{ fontWeight: 'bold' }}>Accès lecture {tournament.name}</span>
                                <X style={{ cursor: 'pointer' }} onClick={() => setShowQr(false)} />
                            </div>
                            <QRCodeSVG value={viewUrl} size={220} />
                            <input
                                readOnly
                                value={viewUrl}
                                style={{
                                    width: '100%',
                                    fontSize: '11px',
                                    padding: '6px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                }}
                                onClick={(e) => (e.target as HTMLInputElement).select()}
                            />
                            <button
                                className={'button'}
                                style={{ background: '#dc2626', width: '100%' }}
                                onClick={handleRevokeViewToken}
                                disabled={revokeViewToken.isPending}
                            >
                                {revokeViewToken.isPending ? 'Désactivation...' : 'Désactiver le lien'}
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'management' && (
                    <>
                        <p></p>
                        <table className={'table-stripped'}>
                            <thead>
                                <tr>
                                    <th>{t('HEAT_NAME')}</th>
                                    <th>{t('HEAT_TYPE')}</th>
                                    <th>{t('HEAT_AGE_CATEGORY')}</th>
                                    <th>{t('HEAT_POINTS')}</th>
                                    <th>{t('HEAT_NB_REGISTERED')}</th>
                                    <th>{t('HEAT_STATUS')}</th>
                                    <th>{t('HEAT_PROGRESSION')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tournament?.heats.map((heat: Heat) => (
                                    <tr key={heat.id} onClick={() => handleHeatClick(heat.id)}>
                                        <td>{heat.name}</td>
                                        <td>{t(`HEATTYPE.${heat.type}`)}</td>
                                        <td>{heat.ageCategory || '-'}</td>
                                        <td>{heat.pointsLimit || '-'}</td>
                                        <td>{heat.nbRegistered}</td>
                                        <td>{t(`HEATSTATUS.${heat.status}`)}</td>
                                        <td>
                                            <LoadingBar progress={heat.progress} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <InputIcon
                            icon={Search}
                            placeholder={t('PLAYER_SEARCH')}
                            type={'text'}
                            value={search}
                            small={true}
                            onChange={(e) => setSearch(e.target.value)}
                        />

                        {players && players?.length > 0 ? (
                            <table className={'table-stripped'}>
                                <thead>
                                    <tr>
                                        <th>Prénom</th>
                                        <th>Nom</th>
                                        <th>Classement</th>
                                        <th>Club</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {players?.map((player: Player) => (
                                        <tr
                                            key={player.id}
                                            onClick={() => handleRegisterClick(tournamentId, player.id || 0)}
                                        >
                                            <td>{player.firstname}</td>
                                            <td>{player.lastname}</td>
                                            <td>{player.points}</td>
                                            <td>{player?.club?.name || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : players && players.length === 0 ? (
                            <h2>{t('search_no_result')}</h2>
                        ) : null}

                        {registrations && registrations.length > 0 ? (
                            <>
                                <h3>
                                    {registrations.length} {t('PLAYER_REGISTERED')}
                                </h3>
                                <table className={'table-stripped'}>
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>{t('PLAYER_LASTNAME')}</th>
                                            <th>{t('PLAYER_FIRSTNAME')}</th>
                                            <th>{t('PLAYER_POINTS')}</th>
                                            <th>{t('PLAYER_CLUB')}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {registrations?.map((player: Player) => (
                                            <tr key={player.id}>
                                                <td>
                                                    <Info onClick={() => handlePlayerClick(player?.id || 0)} />
                                                </td>
                                                <td>{player.lastname}</td>
                                                <td>{player.firstname}</td>
                                                <td>{player.points}</td>
                                                <td>{player?.club?.name || '-'}</td>
                                                <td>
                                                    <UserMinus
                                                        fill="currentColor"
                                                        onClick={() => handleRemoveRegistration(player?.id || 0)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        ) : (
                            <h3>{t('PLAYER_NO_REGISTRATION')}</h3>
                        )}
                    </>
                )}

                {activeTab === 'configuration' && <TournamentPage />}

                {activeTab === 'preregistrations' && <PreRegistrationListPage />}

                {activeTab === 'preregistrationConfig' && <TournamentPreRegistrationConfig />}

                {activeTab === 'email' && <TournamentEmailConfig tournamentId={tournamentId} />}

                {activeTab === 'survey' && (
                    <TournamentSurveyConfig tournamentId={tournamentId} allHeatsFinished={allHeatsFinished} />
                )}

                {activeTab === 'members' && <TournamentMembers />}

                {activeTab === 'tables' && <TournamentTablesPage tournamentId={tournamentId} />}
            </div>
        )
    );
};

export default TournamentDetail;
